import React, { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const TYPE_SPEED = 100;   // ms per character (typing and deleting)
const PAUSE_FULL = 1000;  // hold the full text before deleting
const PAUSE_EMPTY = 300;  // gap between deleting and typing again
const PLACEHOLDER = '$';

/**
 * Time-based typewriter: the visible length is computed from elapsed time,
 * not from chained timeouts. If a phone stalls (particles, 3D model, GC),
 * the animation catches up instead of stretching the pauses.
 */
const TypewriterText = ({ text }) => {
    const [length, setLength] = useState(0);
    const [isMobile, setIsMobile] = useState(() => (
        typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
    ));
    const lastLength = useRef(0);
    const reduceMotion = useReducedMotion();

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 639px)');
        const updateViewport = () => setIsMobile(mediaQuery.matches);
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', updateViewport);
            return () => mediaQuery.removeEventListener('change', updateViewport);
        }

        mediaQuery.addListener(updateViewport);
        return () => mediaQuery.removeListener(updateViewport);
    }, []);

    useEffect(() => {
        const n = text.length;
        if (reduceMotion || n === 0) return;

        const typeDur = n * TYPE_SPEED;
        const cycle = typeDur + PAUSE_FULL + typeDur + PAUSE_EMPTY;
        const start = performance.now();
        let frame;

        const tick = (now) => {
            const t = (now - start) % cycle;
            let next;

            if (t < typeDur) {
                next = Math.floor(t / TYPE_SPEED);                    // typing
            } else if (t < typeDur + PAUSE_FULL) {
                next = n;                                             // hold full text
            } else if (t < typeDur + PAUSE_FULL + typeDur) {
                const td = t - typeDur - PAUSE_FULL;
                next = Math.max(0, n - Math.floor(td / TYPE_SPEED));  // deleting
            } else {
                next = 0;                                             // empty gap
            }

            if (next !== lastLength.current) {
                lastLength.current = next;
                setLength(next);
            }
            frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [text, reduceMotion]);

    const shown = reduceMotion ? text : length === 0 ? PLACEHOLDER : text.slice(0, length);

    return (
        // Entrance fade lives on the heading...
        <motion.h6
            aria-label={text}
            className="font-extrabold mb-4 tracking-widest text-3xl md:text-5xl lg:text-5xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: isMobile ? 0.35 : 1 }}
        >
            {/* ...and the CSS pulse on its own element, so the two never fight over opacity */}
            <span aria-hidden="true" className="inline-block animate-pulse motion-reduce:animate-none">
                {shown}
            </span>
        </motion.h6>
    );
};

export default TypewriterText;
